// API сервис для работы с showcase данными

import { apiFetch } from '@/shared/auth/runtimeAuth';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL?.trim() || 'https://api.upmini.app';
let currentShowcaseProjectId = '';
export const getCurrentShowcaseProjectId = (): string => currentShowcaseProjectId;
const SHOWCASE_LAUNCH_ID_KEY = 'showcase:launch-id';

export function getStoredShowcaseBlockId(): string {
  if (typeof window === 'undefined') return '';
  try {
    return window.sessionStorage.getItem(SHOWCASE_LAUNCH_ID_KEY) || '';
  } catch {
    return '';
  }
}

export function resolveShowcaseBlockId(
  search: string,
  startParam = '',
  sessionFallback = '',
): string {
  const params = new URLSearchParams(search);
  const direct = (
    params.get('id')
    || params.get('block_id')
    || params.get('showcase_id')
    || params.get('project_id')
    || ''
  ).trim();
  if (direct) return direct;

  const startParams = new URLSearchParams(startParam.replace(/^[?#]/, ''));
  return (
    startParams.get('id')
    || startParams.get('block_id')
    || startParams.get('showcase_id')
    || startParams.get('project_id')
    || sessionFallback
  ).trim();
}

export function getShowcaseBlockIdFromUrl(): string {
  const startParam = String(
    (window.Telegram?.WebApp?.initDataUnsafe as { start_param?: string } | undefined)?.start_param
    || '',
  );
  const sessionFallback = getStoredShowcaseBlockId();
  const blockId = resolveShowcaseBlockId(window.location.search, startParam, sessionFallback);
  if (blockId) {
    try {
      window.sessionStorage.setItem(SHOWCASE_LAUNCH_ID_KEY, blockId);
    } catch {
      // Query and Telegram start parameters remain authoritative when storage is unavailable
    }
  }
  return blockId;
}

export interface ApiImage {
  url: string;
  mime_type: string;
  width: number;
  height: number;
}

export interface ApiCategory {
  name: string;
  is_active: boolean;
  id: string;
  order?: number;
  parent_id?: string | null;
}

export interface ApiTag {
  name: string;
  description?: string | null;
  color: string;
  background_color: string;
  id: string;
}

export interface ApiAddon {
  name: string;
  price: number;
  price_minor?: number | null;
  currency: string;
  currency_code?: string;
}

export interface ApiVariation {
  size: string;
  sizeUnit: string | null;
  price: number;
  price_minor?: number | null;
  currency: string;
  currency_code?: string;
  costEnabled: boolean;
  pricingMode?: 'fixed' | 'free' | 'on_request' | 'range';
  priceFrom?: number | null;
  priceTo?: number | null;
}

export interface ApiPositionDiscountConfig {
  enabled: boolean;
  percent: number;
  visible_to_clients: boolean;
  linked_position_id?: string | null;
  linked_discount_percent: number;
  schedule_rules?: Array<{
    id: string;
    is_active: boolean;
    schedule_type: 'date' | 'weekly';
    date?: string | null;
    weekday?: number | null;
    start_time: string;
    end_time: string;
    percent: number;
    timezone: string;
  }>;
}

export interface ApiPosition {
  name: string;
  description: string;
  image: ApiImage | null;
  tags: string[];
  category_ids: string[];
  addons: ApiAddon[];
  recommended_items: string[];
  variations: ApiVariation[];
  attributes?: Array<{ key: string; name?: string; label?: string; value: string | number; unit?: string; comparable?: boolean }>;
  discount_config?: ApiPositionDiscountConfig;
  position_type?: 'product' | 'service' | 'package' | 'information' | 'custom' | 'entitlement' | 'deposit';
  action_type?: 'purchase' | 'application' | 'consultation';
  price_minor?: number | null;
  currency?: string;
  availability_mode?: 'in_stock' | 'limited' | 'made_to_order' | 'pre_order' | 'request_only' | 'unavailable_with_alternative' | 'available' | 'on_request' | 'out_of_stock';
  availability_reason?: string | null;
  lead_time_min_minutes?: number | null;
  lead_time_max_minutes?: number | null;
  alternative_position_id?: string | null;
  stock_quantity?: number | null;
  fulfillment_options?: string[];
  is_active: boolean;
  id: string;
}

export interface ApiCatalogItemV2 {
  pricing_mode?: 'fixed' | 'free' | 'on_request' | 'range';
  price_from?: number | null;
  price_to?: number | null;
  id: string;
  name: string;
  description: string;
  image_url?: string | null;
  item_type: 'goods' | 'service' | 'package' | 'information' | 'custom' | 'entitlement' | 'deposit';
  category_ids: string[];
  tags: string[];
  attributes: Array<Record<string, unknown>>;
  variants: Array<{ id: string; name: string; price: { amount_minor: number; currency: string }; pricing_mode?: "fixed" | "free" | "on_request" | "range"; price_from?: number | null; price_to?: number | null; availability?: ApiCatalogItemV2['availability']; action_contract?: ApiCatalogItemV2['action_contract'] }>;
  modifiers: Array<{ id: string; name: string; type: string; price_delta: { amount_minor: number; currency: string } }>;
  bundle_components: Array<Record<string, unknown>>;
  recommended_item_ids: string[];
  recommendations?: Array<{
    item_id: string;
    rule_code: string;
    reason: string;
    evidence: string[];
  }>;
  fulfillment_options: string[];
  availability: {
    available_quantity?: number | null;
    mode: ApiPosition['availability_mode'];
    reason?: string | null;
    lead_time_min_minutes?: number | null;
    lead_time_max_minutes?: number | null;
    alternative_position_id?: string | null;
    allowed_actions: Array<'buy' | 'request' | 'consult'>;
  };
  action_contract: {
    primary: 'buy' | 'request' | 'consult';
    allowed: Array<'buy' | 'request' | 'consult'>;
    requires_qualification: boolean;
    requires_payment: boolean;
  };
  base_price?: { amount_minor: number; currency: string } | null;
}

export const fetchCatalogV2 = async (showcaseBlockId: string): Promise<ApiCatalogItemV2[]> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/v2/catalog/${showcaseBlockId}`);
  if (!response.ok) throw await responseError(response, 'Не удалось загрузить каталог v2');
  return response.json();
};

export interface ApiBundleDiscount {
  key: number;
  name: string;
  is_active: boolean;
  exclude_other_discounts?: boolean;
  discount_percent: number;
  groups: Array<{
    position_ids: string[];
  }>;
}

export interface ApiShowcaseLocation {
  key: number;
  name: string;
  address: string;
  work_hours: string;
  is_active: boolean;
}

export interface ApiShowcaseData {
  privacy_access?: { policy_approved?: boolean; policy_url?: string | null; machine_code?: string };
  id: string;
  project_id: string;
  block_id: string;
  head: string;
  coordX: number;
  coordY: number;
  dots: Array<{
    centerX: number;
    centerY: number;
    type: string;
    id: string;
  }>;
  created_at: string;
  categories: ApiCategory[];
  positions: ApiPosition[];
  tags: ApiTag[];
  bundle_discounts?: ApiBundleDiscount[];
  order_form?: ApiOrderForm;
  favorites: string[];
  forms?: ApiProfileFormConfig[];
  workflow_settings?: {
    business_category?: 'food' | 'goods' | 'services' | 'beauty' | 'education' | 'other';
    fulfillment_methods?: Array<'delivery' | 'pickup' | 'onsite' | 'online'>;
    locations?: ApiShowcaseLocation[];
    pickup_locations?: string[];
    delivery_rules?: string;
    delivery_min_days?: number;
    delivery_max_days?: number;
    onsite_locations?: string[];
    onsite_rules?: string;
    online_rules?: string;
    online_eta_hours?: number;
    [key: string]: unknown;
  };
}

export type ApiOrderFormField = {
  key: string;
  label: string;
  type: 'string' | 'number' | 'date' | 'time' | 'select' | 'multiselect' | 'text' | 'phone' | 'email' | 'consent';
  required: boolean;
  placeholder: string;
  options: string[];
};

export const updateShowcaseLocations = async (
  showcaseBlockId: string,
  locations: ApiShowcaseLocation[],
): Promise<{ locations: ApiShowcaseLocation[] }> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/showcase-blocks/${showcaseBlockId}/locations`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ locations }),
  });
  if (!response.ok) {
    throw await responseError(response, 'Не удалось сохранить локации');
  }
  return response.json();
};

export const updateShowcaseCapabilities = async (
  showcaseBlockId: string,
  capabilities: Partial<{
    comparison: boolean;
    inventory: boolean;
    show_unavailable_positions: boolean;
  }>,
): Promise<{ capabilities: Record<string, boolean> }> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/showcase-blocks/${showcaseBlockId}/capabilities`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(capabilities),
  });
  if (!response.ok) {
    throw await responseError(response, 'Не удалось сохранить настройки каталога');
  }
  return response.json();
};

export type ApiOrderForm = {
  title: string;
  description: string;
  fields: ApiOrderFormField[];
};

export type ApiProfileField = {
  key?: string | number;
  name?: string;
  label?: string;
  type?: string;
  typeData?: string;
  type_data?: string;
  required?: boolean;
  visible?: boolean;
  placeholder?: string;
  value?: string;
  order?: number;
};

export type ApiProfileFormConfig = {
  form_type?: string;
  field_draft?: ApiProfileField[];
  settings?: Record<string, ApiProfileField[] | string[] | undefined>;
  hidden_field_keys?: Array<string | number>;
};

export const fetchShowcaseData = async (
  projectId: string,
  signal?: AbortSignal,
): Promise<ApiShowcaseData> => {
  try {
    const response = await apiFetch(`${API_BASE_URL}/showcase/showcase-blocks/${projectId}`, { signal });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json() as ApiShowcaseData;
    currentShowcaseProjectId = data.project_id;
    return data;
  } catch (error) {
    const wasAborted = signal?.aborted
      || (error instanceof DOMException && error.name === 'AbortError');
    if (!wasAborted) console.error('Showcase data request failed');
    throw error;
  }
};

// ========== Tags API ==========

export interface ApiTagResponse {
  id: string;
  name: string;
  description?: string | null;
  color: string;
  background_color: string;
}

export const getTag = async (tagId: string): Promise<ApiTagResponse> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/tags/${tagId}`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const updateTag = async (tagId: string, data: Partial<ApiTagResponse>): Promise<ApiTagResponse> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/tags/${tagId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const deleteTag = async (tagId: string): Promise<void> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/tags/${tagId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
};

export const getTagsByBlock = async (showcaseBlockId: string): Promise<ApiTagResponse[]> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/tags/block/${showcaseBlockId}`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const createTag = async (showcaseBlockId: string, data: Omit<ApiTagResponse, 'id'>): Promise<ApiTagResponse> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/tags/block/${showcaseBlockId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

// ========== Favorites API ==========

export interface ApiFavoriteResponse {
  position_id: string;
  telegram_id: string;
  added_at: string; // ISO 8601 datetime string
}

export const getFavorites = async (showcaseBlockId: string, telegramId: string): Promise<ApiFavoriteResponse[]> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/favorites/${showcaseBlockId}/${telegramId}`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export interface CreateFavoriteRequest {
  position_id: string;
  telegram_id: string;
}

export const createFavorite = async (showcaseBlockId: string, data: CreateFavoriteRequest): Promise<ApiFavoriteResponse> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/favorites/${showcaseBlockId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const deleteFavorite = async (positionId: string, telegramId: string): Promise<void> => {
  const showcaseBlockId = getShowcaseBlockIdFromUrl();
  if (!showcaseBlockId) throw new Error('Не удалось определить витрину');
  const response = await apiFetch(`${API_BASE_URL}/showcase/favorites/${showcaseBlockId}/${positionId}?telegram_id=${encodeURIComponent(telegramId)}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
};

// ========== Orders API ==========

export interface ApiOrderItem {
  id?: string; // Опционально при создании, обязательно в ответе
  position_id: string;
  name: string;
  quantity: number;
  price: number | string;
  addons?: ApiAddon[];
  size?: string;
  comment?: string | null;
  client_line_id?: string;
}

export interface ApiOrder {
  id: string;
  status: string;
  telegram_id: string;
  date: string;
  items: ApiOrderItem[];
  username?: string;
  full_name?: string;
  currency?: string;
  details?: Record<string, unknown>;
}

export interface CreateOrderRequest {
  policy_consent: boolean;
  status: string;
  telegram_id?: string;
  username?: string;
  full_name?: string;
  items: ApiOrderItem[];
  details?: Record<string, unknown>;
  idempotency_key?: string;
  expected_quote_hash?: string;
  promo_code?: string;
  loyalty_points?: number;
  certificate_id?: string;
  certificate_code?: string;
  linked_event_id?: string;
  linked_event_product_instance_id?: string;
  promotion_selection?: {
    promotion_id: string;
    gift_position_id: string;
  };
}

export interface OrderQuote {
  subtotal: number;
  discount: number;
  appliedPromotion?: string | null;
  finalAmount: number;
  nonStacking: boolean;
  loyaltyPoints?: number;
  loyaltyDiscount?: number;
  certificateDiscount?: number;
  promotionGift?: {
    promotionId: string;
    promotionName: string;
    positionId: string;
    positionName: string;
  };
  amountMinor?: number;
  quoteHash?: string;
  paymentCollectionMode?: 'online' | 'external';
  onlinePaymentAvailable?: boolean;
  appliedBundles?: Array<{
    applicationId: string;
    ruleKey: string;
    name: string;
    lineIds: string[];
    positionIds: string[];
    positionNames: string[];
    originalAmountMinor: number;
    discountMinor: number;
    finalAmountMinor: number;
  }>;
  loyalty?: {
    rewardType: 'points' | 'discount';
    availablePoints: number;
    maxRedeemablePoints: number;
    maxRedeemPercent: number;
    estimatedEarnPoints: number;
    accrualDelayHours: number;
    pointsLifetimeDays?: number | null;
  };
  deliveryFeePolicy?: {
    requiresManagerConfirmation: boolean;
    enabled: boolean;
    minimumMinor: number;
    maximumMinor: number;
    freeFromMinor?: number | null;
    currency: string;
  };
  requiresManagerConfirmation?: boolean;
}

async function responseError(response: Response, fallback: string): Promise<Error> {
  const payload = await response.json().catch(() => null) as
    | { detail?: string | { message?: string } | Array<{ msg?: string }> }
    | null;
  const detail = Array.isArray(payload?.detail)
    ? payload.detail.map((item) => item.msg).filter(Boolean).join(', ')
    : typeof payload?.detail === 'object'
      ? payload.detail.message
      : payload?.detail;
  return new Error(detail || fallback);
}

export const getOrders = async (showcaseBlockId: string, telegramId: string): Promise<ApiOrder[]> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/orders/${showcaseBlockId}/${telegramId}`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const getAllBlockOrders = async (showcaseBlockId: string): Promise<ApiOrder[]> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/orders/block/${showcaseBlockId}`);
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
};

export const getAssignedOrders = async (showcaseBlockId: string, employeeId: string): Promise<ApiOrder[]> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/orders/assigned/${showcaseBlockId}/${encodeURIComponent(employeeId)}`);
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
};

export const getOpenOrders = async (showcaseBlockId: string): Promise<ApiOrder[]> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/orders/open/${showcaseBlockId}`);
  if (!response.ok) throw await responseError(response, 'Не удалось загрузить доступные заказы');
  return response.json();
};

export const createOrder = async (showcaseBlockId: string, data: CreateOrderRequest): Promise<ApiOrder> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/orders/${showcaseBlockId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw await responseError(response, `Не удалось оформить заказ: HTTP ${response.status}`);
  }
  return response.json();
};

export const quoteOrder = async (
  showcaseBlockId: string,
  data: Pick<CreateOrderRequest, 'telegram_id' | 'username' | 'items' | 'promo_code' | 'loyalty_points' | 'certificate_id' | 'certificate_code' | 'promotion_selection' | 'linked_event_id' | 'linked_event_product_instance_id' | 'details'>,
  signal?: AbortSignal,
): Promise<OrderQuote> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/orders/${showcaseBlockId}/quote`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    signal,
  });
  if (!response.ok) {
    throw await responseError(response, `Не удалось рассчитать заказ: HTTP ${response.status}`);
  }
  return response.json();
};

export const getOrder = async (orderId: string): Promise<ApiOrder> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/orders/${orderId}`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const updateOrder = async (orderId: string, data: Partial<ApiOrder>): Promise<ApiOrder> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/orders/${orderId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export type OrderWorkflowPayload = {
  operation_id?: string;
  status?: 'new' | 'processing' | 'awaiting_payment' | 'awaiting_client_acceptance' | 'returned' | 'completed' | 'cancelled';
  note?: string;
  cancellation_reason?: string;
  payment_amount_minor?: number;
  refund_amount_minor?: number;
  payment_method?: string;
  result?: {
    comment?: string;
    files?: Array<{ name: string; url: string }>;
  };
};

export type OrderConfirmationPayload = {
  operation_id: string;
  assigned_employee_id?: string;
  assigned_employee_name?: string;
  assigned_employee_role?: 'manager' | 'specialist';
  items?: ApiOrderItem[];
  delivery_fee_minor?: number;
  publish_to_specialists?: boolean;
};

export type OrderPaymentHandoff = {
  paymentRequestId: string;
  confirmationUrl: string;
  status: string;
  correlationId: string;
};

export const createOrderPaymentRequest = async (
  orderId: string,
  returnUrl = window.location.href,
): Promise<OrderPaymentHandoff> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/orders/${orderId}/payment-request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      operation_id: `showcase-payment:${orderId}`,
      return_url: returnUrl,
    }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { detail?: string } | null;
    throw new Error(payload?.detail || `Не удалось подготовить оплату: HTTP ${response.status}`);
  }
  return response.json();
};

export const authorizeOrderPayment = async (orderId: string): Promise<ApiOrder> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/orders/${orderId}/payment-authorization`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ operation_id: `authorize-payment:${orderId}` }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { detail?: string } | null;
    throw new Error(payload?.detail || `Не удалось разрешить оплату: HTTP ${response.status}`);
  }
  return response.json() as Promise<ApiOrder>;
};

const pendingWorkflowOperations = new Map<string, string>();

export const updateOrderWorkflow = async (orderId: string, data: OrderWorkflowPayload): Promise<ApiOrder> => {
  const operationKey = `${orderId}:${JSON.stringify(data)}`;
  const operationId = data.operation_id ?? pendingWorkflowOperations.get(operationKey) ?? crypto.randomUUID();
  pendingWorkflowOperations.set(operationKey, operationId);
  const response = await apiFetch(`${API_BASE_URL}/showcase/orders/${orderId}/workflow`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...data, operation_id: operationId }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { detail?: string } | null;
    if (response.status < 500) pendingWorkflowOperations.delete(operationKey);
    throw new Error(payload?.detail || `HTTP error! status: ${response.status}`);
  }
  pendingWorkflowOperations.delete(operationKey);
  return response.json();
};

export const confirmOrder = async (
  orderId: string,
  data: OrderConfirmationPayload,
): Promise<ApiOrder> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/orders/${orderId}/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { detail?: string } | null;
    throw new Error(payload?.detail || `Не удалось подтвердить заказ: HTTP ${response.status}`);
  }
  return response.json();
};

export const claimOrder = async (orderId: string): Promise<ApiOrder> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/orders/${orderId}/claim`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ operation_id: `claim-${orderId}` }),
  });
  if (!response.ok) {
    throw await responseError(response, 'Не удалось взять заказ');
  }
  return response.json();
};

export const returnOrderAssignment = async (orderId: string, reason: string): Promise<ApiOrder> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/orders/${orderId}/return-assignment`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ operation_id: crypto.randomUUID(), reason }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { detail?: string } | null;
    throw new Error(payload?.detail || 'Не удалось вернуть заказ менеджеру');
  }
  return response.json();
};

export const decideOrderResult = async (
  orderId: string,
  accepted: boolean,
  comment?: string,
): Promise<ApiOrder> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/orders/${orderId}/client-decision`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      operation_id: crypto.randomUUID(),
      accepted,
      comment: comment?.trim() || undefined,
    }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { detail?: string } | null;
    throw new Error(payload?.detail || `Не удалось сохранить решение: HTTP ${response.status}`);
  }
  return response.json();
};

export const deleteOrder = async (orderId: string): Promise<void> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/orders/${orderId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
};

// ========== Clients API ==========

export interface ApiShowcaseClient {
  client_id: string;
  full_name: string;
  username?: string | null;
  phone?: string | null;
  email?: string | null;
  city?: string | null;
  about?: string | null;
  assigned_employee_id?: string | null;
  assigned_employee_name?: string | null;
  assigned_employee_role?: 'specialist' | 'manager' | null;
  custom_fields?: Record<string, string | boolean>;
  created_at: string;
  updated_at: string;
}

export type ClientProfileUpdate = {
  full_name?: string;
  phone?: string;
  email?: string;
  city?: string;
  about?: string;
  custom_fields?: Record<string, string | boolean>;
};

export const getShowcaseClients = async (showcaseBlockId: string): Promise<ApiShowcaseClient[]> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/clients/block/${showcaseBlockId}`);
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
};

export const getMyAssignedShowcaseClients = async (showcaseBlockId: string): Promise<ApiShowcaseClient[]> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/clients/block/${showcaseBlockId}/assigned/me`);
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
};

export const getMyShowcaseClient = async (showcaseBlockId: string): Promise<ApiShowcaseClient> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/clients/block/${showcaseBlockId}/me`);
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
};

export const getShowcaseClient = async (showcaseBlockId: string, clientId: string): Promise<ApiShowcaseClient> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/clients/block/${showcaseBlockId}/${encodeURIComponent(clientId)}`);
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
};

export const updateMyShowcaseClient = async (
  showcaseBlockId: string,
  data: ClientProfileUpdate,
): Promise<ApiShowcaseClient> => {
  const response = await apiFetch(`${API_BASE_URL}/showcase/clients/block/${showcaseBlockId}/me`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
};

export const updateShowcaseClientAssignment = async (
  showcaseBlockId: string,
  clientId: string,
  data: {
    assigned_employee_id?: string | null;
    assigned_employee_name?: string | null;
    assigned_employee_role?: 'specialist' | 'manager' | null;
  },
): Promise<ApiShowcaseClient> => {
  const response = await apiFetch(
    `${API_BASE_URL}/showcase/clients/block/${showcaseBlockId}/${encodeURIComponent(clientId)}/assignment`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    },
  );
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
};

// ========== Projects API ==========

export interface ApiProject {
  id: string;
  user_id: string;
  name?: string | null;
  bot_token?: string | null;
  yookass_shop_id?: string | null;
  yookassa_secret_token?: string | null;
  accent_color?: string | null;
  font?: string | null;
}

export const getProject = async (projectId: string): Promise<ApiProject> => {
  const response = await apiFetch(`${API_BASE_URL}/core/projects/${projectId}`);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};
